import React from 'react'
import { Line, Rect, Text, Group, Image } from 'react-konva'
import useImage from 'use-image'
// import useImage from './usePreloadedImages'
import {
  pictoSizeAtom,
  textSizeAtom,
  textSettingsAtom,
  fitzgeraldBackgroundAtom,
  fitzgeraldFrameAtom,
  pictogramsFamilyAtom,
  miscellaneousUpperCaseAtom,
  customImagesFamilyAtom,
} from 'atoms'
import { useAtomValue } from 'jotai'
import { typeColorMapping } from 'constants'

const Pictogram = ({ uuid, x = 0, y = 0 }) => {
  const stageSize = useAtomValue(pictoSizeAtom)
  const { font } = useAtomValue(textSettingsAtom)
  const fontSize = useAtomValue(textSizeAtom)
  const customImage = useAtomValue(customImagesFamilyAtom(uuid))
  const background = useAtomValue(fitzgeraldBackgroundAtom)
  const frame = useAtomValue(fitzgeraldFrameAtom)
  const pictogram = useAtomValue(pictogramsFamilyAtom(uuid))
  const miscellaneousUpperCase = useAtomValue(miscellaneousUpperCaseAtom)

  const {
    caption: rawCaption = '',
    pictos = [],
    outcome = '',
    currentIndex = 0,
    imageURL = '',
    imageAsText = false,
  } = pictogram || {}

  const caption = miscellaneousUpperCase ? rawCaption.toUpperCase() : rawCaption
  const color = typeColorMapping[pictos[currentIndex]?.type] || 'lightgrey'

  const strike =
    (rawCaption?.startsWith('no ') || rawCaption?.startsWith('No ')) &&
    !(outcome?.startsWith('no ') || outcome?.startsWith('No '))

  // Text configuration
  const strokeWidth = 10
  const textWidth = stageSize - 2 * strokeWidth // 10px padding on each side

  const halfStroke = strokeWidth / 4
  const image = imageAsText ? '' : customImage ? customImage : imageURL
  const [imageObj] = useImage(image, 'anonymous')

  const pictoSize = frame ? stageSize - strokeWidth * 2 : stageSize
  const imageX = frame ? strokeWidth : 0

  if (!pictogram) return null //hack because of stale data

  return (
    <Group key={`${x}-${y}-${uuid}`} x={x} y={y}>
      {background && (
        <Rect
          fill={color}
          width={Number(stageSize)}
          height={Number(stageSize)}
        />
      )}

      {image && (
        <Image
          image={imageObj}
          x={imageX}
          y={imageX}
          width={pictoSize}
          height={pictoSize}
        />
      )}

      {!image && caption && (
        <Text
          text={caption}
          fontSize={fontSize * 1.5}
          fontFamily={font}
          width={textWidth}
          x={(stageSize - textWidth) / 2} // Center horizontally
          y={stageSize / 2 - fontSize / 2} // Center vertically
          align="center"
          ellipsis={true} // Ensure ellipsis for overflow
        />
      )}

      {strike && (
        <>
          <Line
            stroke="red"
            strokeWidth={strokeWidth}
            points={[stageSize, 0, 0, stageSize]}
            opacity="1"
          />
          <Line
            stroke="red"
            strokeWidth={strokeWidth}
            points={[0, 0, stageSize, stageSize]}
            opacity="1"
          />
        </>
      )}

      {frame && (
        <>
          {/* // upper horizontal line */}
          <Line
            stroke={color}
            strokeWidth={strokeWidth / 2}
            points={[0, halfStroke, stageSize, halfStroke]} // Top edge
          />
          {/* Right edge */}
          <Line
            stroke={color}
            strokeWidth={strokeWidth / 2}
            points={[
              stageSize - halfStroke,
              0,
              stageSize - halfStroke,
              stageSize,
            ]}
          />

          <Line
            stroke={color}
            strokeWidth={strokeWidth / 2}
            points={[
              stageSize,
              stageSize - halfStroke,
              0,
              stageSize - halfStroke,
            ]} // Bottom edge
          />
          <Line
            stroke={color}
            strokeWidth={strokeWidth / 2}
            points={[
              halfStroke,
              stageSize - halfStroke,
              halfStroke,
              halfStroke,
            ]} // Left edge
          />
        </>
      )}
    </Group>
  )
}

export default Pictogram
